import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as api from "../services/projectApi";
import DateRangePicker from "./DateRangePicker";
import HealthCard from "./HealthCard";
import KpiCard from "./KpiCard";
import AiAnalysisPanel from "./AiAnalysisPanel";
import { fmtMetric, fmtNum, fmtDateTime } from "./format";

// Performance tab (spec §4–9): range control, health, KPI cards, overall
// metrics, platform breakdown — all for the selected reporting range.
export default function PerformanceView({ projectId, currency = "USD", canSync }) {
  const [rangeVal, setRangeVal] = useState({ range: "last_30d", from: "", to: "" });
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const params = useMemo(() => {
    if (rangeVal.range === "custom") return { from: rangeVal.from, to: rangeVal.to };
    return { range: rangeVal.range };
  }, [rangeVal]);

  const customIncomplete = rangeVal.range === "custom" && (!rangeVal.from || !rangeVal.to);

  const load = useCallback(async () => {
    if (customIncomplete) return;
    setLoading(true);
    setError(null);
    try {
      const [summary, kpiStatus, health, sva] = await Promise.all([
        api.getPerformanceSummary(projectId, { ...params, compare: compare ? "previous_period" : undefined }),
        api.getKpiStatus(projectId, params),
        api.getHealth(projectId, params),
        api.getStrategyVsActual(projectId, params),
      ]);
      setData({ summary, kpiStatus, health, sva });
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, params, compare, customIncomplete]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.syncProject(projectId);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const meta = data?.summary?.meta;
  // Prefer the real ad-account currency over the project default (e.g. AUD, not USD).
  const displayCurrency = meta?.currency || currency;
  const overall = data?.summary?.overall;
  const noData = overall && Number(overall.spend || 0) === 0 && Number(overall.impressions || 0) === 0;

  const TILES = overall
    ? [
        { k: "Spend", v: fmtMetric(overall.spend, displayCurrency) },
        { k: "Impressions", v: fmtNum(overall.impressions) },
        { k: "Clicks", v: fmtNum(overall.clicks) },
        { k: "CTR", v: fmtMetric(overall.ctr, "%") },
        { k: "Conversions", v: fmtNum(overall.conversions) },
        { k: "CPA", v: fmtMetric(overall.cpa, displayCurrency) },
        { k: "Revenue", v: fmtMetric(overall.conversionValue, displayCurrency) },
        { k: "ROAS", v: fmtMetric(overall.roas, "x") },
      ]
    : [];

  return (
    <div className="perf">
      <div className="perf-toolbar">
        <DateRangePicker value={rangeVal} onChange={setRangeVal} compare={compare} onCompareChange={setCompare} />
        <div className="perf-freshness">
          {meta?.lastSyncedAt ? (
            <span className="proj-muted">Updated {fmtDateTime(meta.lastSyncedAt)}</span>
          ) : (
            <span className="proj-muted">Not synced yet</span>
          )}
          {meta?.partial && <span className="status-chip tone-warn">Partial data</span>}
          {canSync && (
            <button className="proj-btn" disabled={syncing} onClick={sync}>
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>
      </div>

      {customIncomplete && <div className="proj-alert-soft">Pick both a start and end date.</div>}
      {error && <div className="proj-alert">{error}</div>}
      {loading && <div className="proj-center proj-muted">Loading performance…</div>}

      {!loading && data && (
        <>
          {meta?.failedPlatforms?.length > 0 && (
            <div className="proj-alert-soft">
              Couldn't load data from {meta.failedPlatforms.join(", ")} — showing partial results.
            </div>
          )}

          <HealthCard health={data.health} />

          {noData ? (
            <div className="proj-panel proj-empty-tab">
              <h4>No performance data for this range</h4>
              <p className="proj-muted">
                Link an ad account in <strong>Settings</strong>, then <strong>Sync now</strong> to pull Google Ads and Meta Ads data.
              </p>
            </div>
          ) : (
            <>
              <div className="metric-tiles">
                {TILES.map((t) => (
                  <div key={t.k} className="metric-tile">
                    <span className="metric-k">{t.k}</span>
                    <span className="metric-v">{t.v}</span>
                  </div>
                ))}
              </div>

              {data.kpiStatus?.kpis?.length > 0 && (
                <>
                  <h4 className="perf-section-title">KPIs vs targets</h4>
                  <div className="kpi-cards">
                    {data.kpiStatus.kpis.map((k) => (
                      <KpiCard key={k.id} kpi={k} />
                    ))}
                  </div>
                </>
              )}

              {data.summary.platforms?.length > 0 && (
                <>
                  <h4 className="perf-section-title">By platform</h4>
                  <div className="tscroll">
                    <table className="perf-table">
                      <thead>
                        <tr>
                          <th>Platform</th><th>Spend</th><th>Impressions</th><th>Clicks</th>
                          <th>CTR</th><th>Conv.</th><th>CPA</th><th>Revenue</th><th>ROAS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.summary.platforms.map(({ platform, metrics: m }) => (
                          <tr key={platform}>
                            <td className="cap">{platform}</td>
                            <td>{fmtMetric(m.spend, displayCurrency)}</td>
                            <td>{fmtNum(m.impressions)}</td>
                            <td>{fmtNum(m.clicks)}</td>
                            <td>{fmtMetric(m.ctr, "%")}</td>
                            <td>{fmtNum(m.conversions)}</td>
                            <td>{fmtMetric(m.cpa, displayCurrency)}</td>
                            <td>{fmtMetric(m.conversionValue, displayCurrency)}</td>
                            <td>{fmtMetric(m.roas, "x")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {data.sva && (
                <div className="proj-panel sva-panel">
                  <h4>Strategy vs actual</h4>
                  <div className="sva-grid">
                    {data.sva.budget && (
                      <div>
                        <span className="kpi-k">Budget split · planned → actual</span>
                        <p>
                          Google {data.sva.budget.planned.google}% → {data.sva.budget.actual.google ?? "—"}% · Meta{" "}
                          {data.sva.budget.planned.meta}% → {data.sva.budget.actual.meta ?? "—"}%
                        </p>
                        <p className={`sva-note ${data.sva.budget.onPlan === false ? "off" : ""}`}>{data.sva.budget.note}</p>
                      </div>
                    )}
                    <div>
                      <span className="kpi-k">KPIs vs plan</span>
                      <p>
                        {data.sva.kpis.meeting.length} on track/ahead · {data.sva.kpis.behind.length} behind ·{" "}
                        {data.sva.kpis.notMeasured.length} no data
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {data.summary.comparison && (
                <p className="perf-compare-note">
                  {data.summary.comparison.hasData
                    ? `vs previous period (${data.summary.comparison.range.from} → ${data.summary.comparison.range.to}): spend ${fmtMetric(data.summary.comparison.overall.spend, displayCurrency)}, ROAS ${fmtMetric(data.summary.comparison.overall.roas, "x")}.`
                    : "No data for the previous period to compare against."}
                </p>
              )}
            </>
          )}

          <AiAnalysisPanel projectId={projectId} rangeParams={{ ...params, compare: compare ? "previous_period" : undefined }} />
        </>
      )}
    </div>
  );
}
